// Mock data for CoachMeUpp landing page

// Client logos for scrolling banner
export const clientLogos = [
  { name: "TechCorp" },
  { name: "GlobalBank" },
  { name: "InnovateInc" },
  { name: "FutureRetail" },
  { name: "HealthPlus" },
  { name: "EduTech" },
  { name: "MediaGroup" },
  { name: "LogisticsX" }
];

export const heroData = {
  title: "AI-Powered Digital Talent Management Platform",
  subtitle: "Transform your workforce with scalable coaching, real-time ROI tracking, and comprehensive analytics that prove measurable business impact",
  cta: [
    { text: "Book a Demo", type: "primary", action: "demo" }
  ],
  heroImage: "https://images.pexels.com/photos/5717626/pexels-photo-5717626.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  features: [
    {
      icon: "LineChart",
      title: "Automated ROI Calculation",
      description: "Real-time tracking of coaching program impact on business metrics"
    },
    {
      icon: "BarChart3",
      title: "Advanced Reporting",
      description: "Comprehensive dashboards with actionable insights for leadership"
    },
    {
      icon: "TrendingUp",
      title: "Performance Analytics",
      description: "Track employee development, engagement, and productivity gains"
    }
  ]
};

export const trustIndicators = {
  title: "Trusted By Leading Organizations",
  stats: [
    { value: "25+", label: "Companies Transformed" },
    { value: "250+", label: "Leaders Coached" },
    { value: "95%", label: "Client Satisfaction" },
    { value: "4.9/5", label: "Average Rating" }
  ],
  certifications: [
    { name: "ICF Certified", logo: "ICF" },
    { name: "EMCC Accredited", logo: "EMCC" },
    { name: "ACC Certified", logo: "ACC" },
    { name: "ISO 9001", logo: "ISO" }
  ]
};

export const benefitsData = {
  title: "Real Value. Measurable Results.",
  subtitle: "Companies with digital coaching develop a positive culture of change that transforms the entire organization",
  metrics: [
    {
      percentage: "62%",
      title: "Higher Retention of Top Performers",
      description: "Keep your best talent engaged and growing with personalized development paths",
      source: "ICF / HCI, 2019",
      icon: "TrendingUp"
    },
    {
      percentage: "65%",
      title: "Increased Profitability",
      description: "Drive bottom-line results through improved leadership effectiveness and team performance",
      source: "ICF / HCI, 2019",
      icon: "DollarSign"
    },
    {
      percentage: "85%",
      title: "Higher Customer Satisfaction",
      description: "Better leaders create better customer experiences and lasting relationships",
      source: "ICF / HCI, 2019",
      icon: "Heart"
    },
    {
      percentage: "3.5x",
      title: "ROI on Coaching Investment",
      description: "Measurable returns through enhanced productivity, engagement, and performance",
      source: "ICF Global Study, 2020",
      icon: "Target"
    }
  ],
  keyBenefits: [
    {
      title: "Leadership Development",
      description: "Build confident, effective leaders who inspire and drive results across all levels of your organization",
      icon: "Users"
    },
    {
      title: "Employee Engagement",
      description: "Create a culture of growth and belonging that attracts and retains top talent",
      icon: "Heart"
    },
    {
      title: "Performance Improvement",
      description: "Unlock individual and team potential with data-driven coaching strategies",
      icon: "BarChart3"
    },
    {
      title: "Organizational Transformation",
      description: "Drive systemic change through scalable development programs that align with business goals",
      icon: "Zap"
    }
  ]
};

export const processData = {
  title: "Our Coaching Process with Built-In ROI Tracking",
  subtitle: "Every company faces unique challenges and requires unique solutions. We'll work together to personalize your CoachMeUpp experience with continuous ROI measurement at every step.",
  steps: [
    {
      number: "01",
      title: "Program Design & KPI Setup",
      description: "We co-create a personalized, AI-powered coaching program with custom KPIs and baseline metrics to measure ROI from day one.",
      icon: "Lightbulb"
    },
    {
      number: "02",
      title: "Participant Selection",
      description: "Collaborate with managers across your organization to identify and select participants who will benefit most from the coaching program.",
      icon: "UserCheck"
    },
    {
      number: "03",
      title: "Coach Matching",
      description: "AI-powered matching algorithm pairs employees with certified coaches based on goals, learning style, and development needs.",
      icon: "Users"
    },
    {
      number: "04",
      title: "Platform Access & Tracking",
      description: "Members access the CoachMeUpp platform where every interaction, goal, and milestone is automatically tracked for ROI analysis.",
      icon: "Monitor"
    },
    {
      number: "05",
      title: "Session Scheduling",
      description: "Members self-direct their programs with unlimited coaching sessions. All engagement metrics feed into your analytics dashboard.",
      icon: "Calendar"
    },
    {
      number: "06",
      title: "Automated ROI Reports",
      description: "CoachMeUpp Insights™ provides real-time ROI calculation, executive dashboards, impact reports, and business metric correlations automatically.",
      icon: "LineChart"
    }
  ]
};

export const servicesData = {
  title: "Comprehensive Coaching Solutions with ROI Analytics",
  subtitle: "Flexible programs designed to meet your organization's unique development needs - all with built-in reporting and ROI tracking",
  services: [
    {
      title: "Executive Coaching",
      description: "One-on-one coaching for C-suite and senior leaders to enhance strategic thinking, decision-making, and organizational impact",
      features: [
        "Personalized development plans",
        "360-degree feedback integration",
        "Strategic goal alignment",
        "Confidential executive sessions"
      ],
      icon: "Crown",
      popular: false
    },
    {
      title: "Leadership Development",
      description: "Scalable programs for emerging and mid-level leaders to build essential management and leadership capabilities",
      features: [
        "Core leadership competencies",
        "Team management skills",
        "Communication excellence",
        "Change management"
      ],
      icon: "TrendingUp",
      popular: true
    },
    {
      title: "Team Coaching",
      description: "Group coaching sessions that strengthen team dynamics, collaboration, and collective performance",
      features: [
        "Team alignment workshops",
        "Collaborative problem-solving",
        "Trust building exercises",
        "Performance optimization"
      ],
      icon: "Users",
      popular: false
    },
    {
      title: "High-Potential Programs",
      description: "Accelerated development for high-potential employees to prepare them for future leadership roles",
      features: [
        "Fast-track development",
        "Succession planning support",
        "Cross-functional exposure",
        "Mentorship integration"
      ],
      icon: "Star",
      popular: false
    }
  ]
};

export const testimonialsData = {
  title: "Success Stories",
  subtitle: "See how leading organizations are transforming their talent development with CoachMeUpp",
  testimonials: [
    {
      quote: "CoachMeUpp has revolutionized how we approach leadership development. The scalability and measurable impact have been game-changing for our organization.",
      author: "Sarah Johnson",
      role: "Chief People Officer",
      company: "Global Tech Corp",
      avatar: "https://images.pexels.com/photos/1367274/pexels-photo-1367274.jpeg",
      results: "45% increase in leadership readiness scores"
    },
    {
      quote: "The AI-powered coach matching and real-time insights have made our coaching program more effective and easier to manage than ever before.",
      author: "Michael Chen",
      role: "VP of Talent Development",
      company: "Innovation Industries",
      avatar: "https://images.pexels.com/photos/7793999/pexels-photo-7793999.jpeg",
      results: "87% employee engagement rate"
    },
    {
      quote: "We've seen remarkable improvements in both employee satisfaction and business results. The ROI is clear and compelling.",
      author: "Emma Rodriguez",
      role: "Director of L&D",
      company: "Enterprise Solutions Inc",
      avatar: "https://images.pexels.com/photos/6950015/pexels-photo-6950015.jpeg",
      results: "4.2x ROI in first year"
    }
  ]
};

export const faqData = {
  title: "Frequently Asked Questions",
  subtitle: "Everything you need to know about CoachMeUpp",
  faqs: [
    {
      question: "How does the AI-powered coach matching work?",
      answer: "Our proprietary AI algorithm analyzes participant goals, preferences, learning styles, and organizational context to match them with the most suitable coach from our global network. The system considers factors like industry experience, coaching specialties, availability, and past success rates to ensure optimal pairings."
    },
    {
      question: "What makes CoachMeUpp different from traditional coaching?",
      answer: "CoachMeUpp combines the best of human coaching expertise with cutting-edge technology. Unlike traditional coaching, we offer scalability across your entire organization, real-time analytics, flexible scheduling, AI-powered insights, and measurable ROI tracking—all through a single integrated platform."
    },
    {
      question: "How do you measure the success of coaching programs?",
      answer: "We use CoachMeUpp Insights™ to provide real-time visibility into program effectiveness. Our platform tracks engagement metrics, goal achievement, competency development, 360-degree feedback changes, and business impact indicators. You'll receive comprehensive dashboards and reports demonstrating clear ROI."
    },
    {
      question: "What qualifications do your coaches have?",
      answer: "All CoachMeUpp coaches are certified by leading international coaching associations (ICF, EMCC, ACC) and have at least 6 years of professional coaching experience. They undergo rigorous vetting, including credential verification, reference checks, and demonstration sessions before joining our network."
    },
    {
      question: "Can the platform integrate with our existing HR systems?",
      answer: "Yes, CoachMeUpp offers seamless integration with major HRIS, LMS, and performance management systems including Workday, SAP SuccessFactors, Oracle HCM, and others. Our API-first architecture ensures smooth data flow and single sign-on capabilities."
    },
    {
      question: "How quickly can we launch a coaching program?",
      answer: "Most organizations can launch within 2-4 weeks. Our implementation team works with you to customize the platform, onboard participants, match coaches, and train administrators. We provide white-glove support throughout the launch process to ensure success from day one."
    },
    {
      question: "What kind of support do you provide after launch?",
      answer: "We offer comprehensive ongoing support including dedicated customer success managers, 24/7 technical support, regular program reviews, coach quality assurance, participant engagement strategies, and continuous platform updates based on your feedback."
    },
    {
      question: "Is there a minimum company size or commitment required?",
      answer: "While CoachMeUpp is designed for mid-to-large enterprises, we work with organizations of various sizes. Minimum commitment typically starts at 20 participants for 6 months, but we can customize programs based on your specific needs and growth plans."
    }
  ]
};

export const pricingData = {
  title: "Flexible Pricing for Every Organization",
  subtitle: "Choose the plan that fits your talent development goals",
  plans: [
    {
      name: "Starter",
      description: "Perfect for teams ready to pilot coaching",
      price: "Custom",
      period: "",
      features: [
        "20-50 participants",
        "AI-powered coach matching",
        "Unlimited coaching sessions",
        "Basic analytics dashboard",
        "Email support",
        "6-month minimum commitment"
      ],
      cta: "Contact Sales",
      popular: false
    },
    {
      name: "Professional",
      description: "Ideal for scaling coaching across departments",
      price: "Custom",
      period: "",
      features: [
        "51-200 participants",
        "Advanced AI matching algorithm",
        "Unlimited coaching sessions",
        "CoachMeUpp Insights™ analytics",
        "Dedicated success manager",
        "HRIS integration",
        "Priority support",
        "12-month commitment"
      ],
      cta: "Book a Demo",
      popular: true
    },
    {
      name: "Enterprise",
      description: "For organizations transforming at scale",
      price: "Custom",
      period: "",
      features: [
        "200+ participants",
        "Custom AI model training",
        "Unlimited coaching sessions",
        "Advanced analytics & reporting",
        "Executive success team",
        "Full system integration",
        "White-glove support",
        "API access",
        "Custom contract terms"
      ],
      cta: "Contact Us",
      popular: false
    }
  ],
  note: "All plans include access to our global network of certified coaches and regular platform updates"
};

export const ctaData = {
  primary: {
    title: "Transform Your Organization Today",
    subtitle: "Join hundreds of leading companies that trust CoachMeUpp for scalable, measurable leadership development",
    ctas: [
      { text: "Book a Demo", action: "demo", type: "primary" },
      { text: "Start Free Trial", action: "trial", type: "secondary" },
      { text: "Talk to Sales", action: "contact", type: "outline" }
    ]
  },
  secondary: {
    title: "Ready to See CoachMeUpp in Action?",
    subtitle: "Schedule a personalized demo and discover how we can help achieve your talent development goals",
    image: "https://images.unsplash.com/photo-1531973576160-7125cd663d86"
  }
};

export const contactData = {
  title: "Get in Touch",
  subtitle: "Have questions? We're here to help you get started",
  formFields: [
    { name: "fullName", label: "Full Name", type: "text", required: true },
    { name: "email", label: "Work Email", type: "email", required: true },
    { name: "company", label: "Company Name", type: "text", required: true },
    { name: "jobTitle", label: "Job Title", type: "text", required: false },
    { name: "companySize", label: "Company Size", type: "select", required: true, options: [
      "1-50 employees",
      "51-200 employees",
      "201-500 employees",
      "501-1000 employees",
      "1000+ employees"
    ]},
    { name: "interest", label: "I'm interested in", type: "select", required: true, options: [
      "Booking a demo",
      "Pricing information",
      "Partnership opportunities",
      "General inquiry"
    ]},
    { name: "message", label: "Message", type: "textarea", required: false }
  ]
};

export const footerData = {
  sections: [
    {
      title: "Platform",
      links: [
        { text: "How It Works", url: "#process" },
        { text: "Our Coaches", url: "#coaches" },
        { text: "Success Stories", url: "#testimonials" }
      ]
    },
    {
      title: "Solutions",
      links: [
        { text: "Executive Coaching", url: "#services" },
        { text: "Leadership Development", url: "#services" },
        { text: "Team Coaching", url: "#services" },
        { text: "High-Potential Programs", url: "#services" }
      ]
    },
    {
      title: "Resources",
      links: [
        { text: "Blog", url: "/blog" },
        { text: "Case Studies", url: "/case-studies" },
        { text: "Whitepapers", url: "/resources" }
      ]
    },
    {
      title: "Company",
      links: [
        { text: "Careers", url: "/careers" },
        { text: "Contact", url: "#contact" },
        { text: "Privacy Policy", url: "/privacy" },
        { text: "Terms of Service", url: "/terms" }
      ]
    }
  ],
  social: [
    { platform: "LinkedIn", url: "https://linkedin.com/company/coachmeupp", icon: "Linkedin" }
  ]
};

// Blog Data
export const blogData = {
  title: "Insights & Resources",
  subtitle: "Expert perspectives on leadership development, coaching, and organizational transformation",
  posts: [
    {
      id: "roi-of-executive-coaching",
      title: "The ROI of Executive Coaching: A Data-Driven Analysis",
      excerpt: "Discover how organizations are achieving 3.5x returns on their coaching investments through measurable outcomes and strategic implementation.",
      content: `
        <p>Executive coaching has evolved from a remedial intervention to a strategic investment in leadership development. Organizations worldwide are recognizing that the return on investment (ROI) from coaching programs far exceeds traditional training methods.</p>
        
        <h2>Key Findings from Our Research</h2>
        <p>Our analysis of over 500 coaching engagements reveals compelling evidence for the business impact of executive coaching:</p>
        <ul>
          <li><strong>3.5x average ROI</strong> on coaching investment within 12 months</li>
          <li><strong>62% improvement</strong> in retention of top performers</li>
          <li><strong>85% increase</strong> in customer satisfaction scores</li>
          <li><strong>45% boost</strong> in leadership readiness assessments</li>
        </ul>
        
        <h2>Measuring What Matters</h2>
        <p>The key to demonstrating coaching ROI lies in establishing clear metrics from the outset. Successful organizations track:</p>
        <ul>
          <li>Pre and post 360-degree feedback scores</li>
          <li>Employee engagement and retention rates</li>
          <li>Business performance indicators tied to coached leaders</li>
          <li>Time to promotion and leadership pipeline metrics</li>
        </ul>
        
        <h2>Best Practices for Maximizing ROI</h2>
        <p>To achieve optimal returns from your coaching investment, consider these strategies:</p>
        <ol>
          <li>Align coaching goals with business objectives</li>
          <li>Select coaches with relevant industry experience</li>
          <li>Implement robust measurement frameworks</li>
          <li>Ensure stakeholder involvement throughout the process</li>
        </ol>
        
        <p>Ready to see the ROI potential for your organization? Contact us for a personalized assessment.</p>
      `,
      author: "Dr. Sarah Mitchell",
      authorRole: "Chief Learning Officer",
      date: "2026-02-15",
      readTime: "6 min read",
      category: "ROI & Analytics",
      image: "https://images.pexels.com/photos/7651553/pexels-photo-7651553.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
      featured: true
    },
    {
      id: "ai-powered-coach-matching",
      title: "How AI is Revolutionizing Coach-Client Matching",
      excerpt: "Learn how artificial intelligence is transforming the way organizations match employees with the right coaches for maximum impact.",
      content: `
        <p>The traditional approach to coach matching—often based on availability or gut feeling—is being replaced by sophisticated AI algorithms that analyze multiple factors to create optimal pairings.</p>
        
        <h2>The Science Behind AI Matching</h2>
        <p>Modern AI matching systems consider numerous variables:</p>
        <ul>
          <li>Coaching goals and development areas</li>
          <li>Learning style preferences</li>
          <li>Industry and functional expertise requirements</li>
          <li>Communication style compatibility</li>
          <li>Historical success patterns</li>
        </ul>
        
        <h2>Results That Speak for Themselves</h2>
        <p>Organizations using AI-powered matching report:</p>
        <ul>
          <li><strong>40% higher</strong> coaching satisfaction scores</li>
          <li><strong>35% faster</strong> goal achievement</li>
          <li><strong>50% reduction</strong> in coach-client mismatches</li>
        </ul>
        
        <h2>The Human Element</h2>
        <p>While AI provides powerful recommendations, the human element remains crucial. The best systems combine algorithmic precision with human judgment, allowing for final approval and adjustments based on contextual factors that algorithms might miss.</p>
        
        <p>Interested in experiencing AI-powered coaching? Schedule a demo to see our matching algorithm in action.</p>
      `,
      author: "Michael Chen",
      authorRole: "VP of Product Innovation",
      date: "2026-02-10",
      readTime: "5 min read",
      category: "Technology",
      image: "https://images.pexels.com/photos/8386440/pexels-photo-8386440.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
      featured: true
    },
    {
      id: "building-coaching-culture",
      title: "Building a Coaching Culture: A Step-by-Step Guide",
      excerpt: "Transform your organization by embedding coaching principles into your company DNA. Here's how leading companies are making it happen.",
      content: `
        <p>A coaching culture goes beyond having coaching programs—it's about creating an environment where coaching conversations happen naturally at every level of the organization.</p>
        
        <h2>What is a Coaching Culture?</h2>
        <p>In a true coaching culture:</p>
        <ul>
          <li>Leaders at all levels adopt a coach approach</li>
          <li>Feedback flows freely in all directions</li>
          <li>Learning from mistakes is celebrated</li>
          <li>Growth mindset is embedded in daily interactions</li>
        </ul>
        
        <h2>The Five Pillars of Coaching Culture</h2>
        <ol>
          <li><strong>Leadership Commitment:</strong> Senior leaders model coaching behaviors</li>
          <li><strong>Skills Development:</strong> Managers trained in coaching techniques</li>
          <li><strong>Systems Support:</strong> Performance management aligned with coaching</li>
          <li><strong>Recognition:</strong> Coaching behaviors are rewarded</li>
          <li><strong>Measurement:</strong> Progress is tracked and celebrated</li>
        </ol>
        
        <h2>Getting Started</h2>
        <p>Begin your coaching culture transformation with these steps:</p>
        <ol>
          <li>Assess your current culture using our diagnostic tool</li>
          <li>Identify coaching champions across the organization</li>
          <li>Launch pilot programs in receptive departments</li>
          <li>Scale based on learnings and successes</li>
        </ol>
        
        <p>Ready to transform your organization? Let's discuss how CoachMeUpp can help build your coaching culture.</p>
      `,
      author: "Emma Rodriguez",
      authorRole: "Director of Organizational Development",
      date: "2026-02-05",
      readTime: "7 min read",
      category: "Culture & Strategy",
      image: "https://images.pexels.com/photos/3184291/pexels-photo-3184291.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
      featured: false
    },
    {
      id: "leadership-development-trends-2026",
      title: "Top Leadership Development Trends for 2026",
      excerpt: "Stay ahead of the curve with insights into the emerging trends shaping how organizations develop their leaders.",
      content: `
        <p>The landscape of leadership development continues to evolve rapidly. Here are the trends that will define excellence in 2026 and beyond.</p>
        
        <h2>1. Hybrid Coaching Models</h2>
        <p>The future of coaching combines the best of virtual and in-person experiences, offering flexibility without sacrificing connection and impact.</p>
        
        <h2>2. Data-Driven Development</h2>
        <p>Organizations are leveraging analytics to personalize development journeys and demonstrate clear ROI on their investments.</p>
        
        <h2>3. Inclusive Leadership Focus</h2>
        <p>DEI competencies are becoming central to leadership development, with coaching programs specifically addressing inclusive leadership skills.</p>
        
        <h2>4. Wellbeing Integration</h2>
        <p>Mental health and resilience are now core components of leadership development, recognizing that sustainable performance requires whole-person support.</p>
        
        <h2>5. Peer Learning Networks</h2>
        <p>Organizations are complementing individual coaching with peer cohorts, creating powerful learning communities.</p>
        
        <p>Want to ensure your leadership development strategy is future-ready? Connect with our experts for a strategic consultation.</p>
      `,
      author: "Dr. James Park",
      authorRole: "Head of Research",
      date: "2026-01-28",
      readTime: "4 min read",
      category: "Trends & Insights",
      image: "https://images.pexels.com/photos/3184339/pexels-photo-3184339.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
      featured: false
    }
  ],
  categories: ["All", "ROI & Analytics", "Technology", "Culture & Strategy", "Trends & Insights"]
};

// Case Studies Data
export const caseStudiesData = {
  title: "Client Success Stories",
  subtitle: "See how leading organizations have transformed their leadership development with CoachMeUpp",
  studies: [
    {
      id: "techcorp-leadership-transformation",
      company: "TechCorp Global",
      industry: "Technology",
      logo: "TC",
      heroImage: "https://images.pexels.com/photos/3183150/pexels-photo-3183150.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
      challenge: "TechCorp was experiencing 35% annual turnover in their senior engineering leadership, costing millions in recruitment and lost productivity. Their traditional training programs weren't addressing the unique challenges of leading technical teams in a fast-paced environment.",
      solution: "We implemented a comprehensive 12-month executive coaching program for 50 senior leaders, combining AI-powered coach matching with structured development paths focused on technical leadership competencies.",
      results: [
        { metric: "68%", label: "Reduction in leadership turnover" },
        { metric: "$2.4M", label: "Annual savings in recruitment costs" },
        { metric: "92%", label: "Participant satisfaction rate" },
        { metric: "4.2x", label: "ROI within first year" }
      ],
      testimonial: {
        quote: "CoachMeUpp transformed how we develop technical leaders. The AI matching ensured each leader was paired with a coach who truly understood the challenges of leading engineering teams. The ROI has been remarkable.",
        author: "Jennifer Walsh",
        role: "Chief People Officer",
        avatar: "JW"
      },
      timeline: "12 months",
      participants: "50 senior leaders",
      programs: ["Executive Coaching", "Leadership Development"]
    },
    {
      id: "globalbank-culture-change",
      company: "GlobalBank Financial",
      industry: "Financial Services",
      logo: "GB",
      heroImage: "https://images.pexels.com/photos/3184292/pexels-photo-3184292.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
      challenge: "Following a major merger, GlobalBank needed to unify leadership approaches across 15,000 employees from two distinct corporate cultures. Employee engagement scores had dropped to 52%, and there was significant resistance to change.",
      solution: "We deployed a scalable coaching culture program, training 200 internal coach-leaders while providing external coaching for the top 100 executives. Our platform enabled consistent tracking and reporting across all regions.",
      results: [
        { metric: "85%", label: "Employee engagement score (up from 52%)" },
        { metric: "200", label: "Internal coaches certified" },
        { metric: "45%", label: "Faster cultural integration" },
        { metric: "3.8x", label: "ROI on coaching investment" }
      ],
      testimonial: {
        quote: "The merger could have been disastrous for our culture. CoachMeUpp helped us create a unified leadership approach that honored both legacy cultures while building something new and stronger.",
        author: "Michael Torres",
        role: "Global Head of Talent",
        avatar: "MT"
      },
      timeline: "18 months",
      participants: "300 leaders across all levels",
      programs: ["Culture Transformation", "Internal Coach Training", "Executive Coaching"]
    },
    {
      id: "healthplus-high-potential",
      company: "HealthPlus Systems",
      industry: "Healthcare",
      logo: "HP",
      heroImage: "https://images.pexels.com/photos/3184418/pexels-photo-3184418.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
      challenge: "HealthPlus had identified 75 high-potential employees for their leadership pipeline but lacked a structured development approach. Previous programs had a 40% dropout rate, and only 25% of participants were promoted within two years.",
      solution: "We created a customized high-potential accelerator program combining group coaching cohorts, individual executive coaching, and AI-driven development recommendations based on real-time progress tracking.",
      results: [
        { metric: "78%", label: "Promotion rate within 18 months" },
        { metric: "95%", label: "Program completion rate" },
        { metric: "62%", label: "Improvement in leadership readiness scores" },
        { metric: "Zero", label: "High-potential turnover during program" }
      ],
      testimonial: {
        quote: "Our high-potential program went from a nice-to-have to our most strategic talent initiative. The combination of personalized coaching and data-driven insights made all the difference.",
        author: "Dr. Amanda Chen",
        role: "VP of Organizational Development",
        avatar: "AC"
      },
      timeline: "18 months",
      participants: "75 high-potential leaders",
      programs: ["High-Potential Development", "Group Coaching", "Executive Coaching"]
    }
  ]
};

// Resources Data
export const resourcesData = {
  title: "Resources & Downloads",
  subtitle: "Access our library of guides, whitepapers, and tools to enhance your leadership development strategy",
  categories: ["All", "Whitepapers", "Guides", "Templates"],
  resources: [
    {
      id: "executive-coaching-roi-guide",
      title: "The Complete Guide to Measuring Coaching ROI",
      description: "A comprehensive framework for calculating and demonstrating the return on investment from your coaching programs, including templates and benchmarks.",
      category: "Whitepapers",
      image: "https://images.pexels.com/photos/590022/pexels-photo-590022.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
      pages: 32,
      downloadUrl: "/resources/executive-coaching-roi-guide.pdf",
      featured: true
    },
    {
      id: "leadership-assessment-template",
      title: "Leadership Competency Assessment Template",
      description: "Ready-to-use assessment framework for evaluating leadership competencies across your organization. Includes scoring guides and development recommendations.",
      category: "Templates",
      image: "https://images.pexels.com/photos/669615/pexels-photo-669615.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
      pages: 15,
      downloadUrl: "/resources/leadership-assessment-template.pdf",
      featured: true
    },
    {
      id: "coaching-culture-playbook",
      title: "Building a Coaching Culture: Implementation Guide",
      description: "Step-by-step guide to embedding coaching principles throughout your organization, from executive sponsorship to front-line manager training.",
      category: "Guides",
      image: "https://images.pexels.com/photos/3184287/pexels-photo-3184287.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
      pages: 28,
      downloadUrl: "/resources/coaching-culture-playbook.pdf",
      featured: false
    },
    {
      id: "digital-coaching-trends-2024",
      title: "2026 Leadership Development Trends Report",
      description: "Our annual research report covering emerging trends, best practices, and predictions for the future of leadership development and executive coaching.",
      category: "Research Reports",
      image: "https://images.pexels.com/photos/7681091/pexels-photo-7681091.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
      pages: 45,
      downloadUrl: "/resources/digital-coaching-trends-2024.pdf",
      featured: true
    },
    {
      id: "executive-coaching-roi-guide",
      title: "Executive Coach Selection Checklist",
      description: "Essential criteria and questions for evaluating and selecting executive coaches for your organization. Includes vendor comparison matrix.",
      category: "Templates",
      image: "https://images.pexels.com/photos/3183197/pexels-photo-3183197.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
      pages: 8,
      downloadUrl: "/resources/executive-coaching-roi-guide.pdf",
      featured: false
    },
    {
      id: "coaching-culture-playbook",
      title: "New Leader Onboarding Coaching Guide",
      description: "Best practices for accelerating new leader success through targeted coaching interventions during the critical first 90 days.",
      category: "Guides",
      image: "https://images.pexels.com/photos/3184339/pexels-photo-3184339.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
      pages: 22,
      downloadUrl: "/resources/coaching-culture-playbook.pdf",
      featured: false
    }
  ]
};
